import { useMemo, useState, useContext } from "react";
import { PiRecordFill } from "react-icons/pi";
import { FiChevronDown, FiChevronRight, FiDownload } from "react-icons/fi";
import AuthContext from "../../context/AuthContext.js";
import DownloadPDF from "../../utils/DownloadPDF.js";

const TempLogTable = ({ temperatureLogs, trainId, alarms, warnings }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const { selectedSite, selectedSiteName } = useContext(AuthContext);

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  const handleDownload = () => {
    const trainDetails = { TrainId: trainId };
    DownloadPDF(selectedSite, selectedSiteName, trainDetails, setIsLoading);
  };

  const formatTemperature = (temp) => {
    return temp !== undefined && temp !== null
      ? parseFloat(temp).toFixed(2)
      : "N/A";
  };

  const getTempStatusIcon = (alarm, warning) => {
    if (alarm) return <PiRecordFill className="ml-1 text-red-500" />;
    if (warning) return <PiRecordFill className="ml-1 text-yellow-500" />;
    return null;
  };

  const calculateTempDiff = (temp1, temp2) => {
    if (temp1 !== undefined && temp2 !== undefined) {
      return Math.abs(parseFloat(temp1) - parseFloat(temp2)).toFixed(2);
    }
    return "N/A";
  };

  const calculateAverage = (temp1, temp2) => {
    if (
      temp1 !== undefined &&
      temp2 !== undefined &&
      temp1 !== null &&
      temp2 !== null
    ) {
      return ((parseFloat(temp1) + parseFloat(temp2)) / 2).toFixed(2);
    }
    return "N/A";
  };

  const shouldShowAxleColumns = useMemo(() => {
    return temperatureLogs.some((log) => log.Nooftempsens >= 2);
  }, [temperatureLogs]);

  const shouldShowWheelColumns = useMemo(() => {
    return temperatureLogs.some((log) => log.Nooftempsens >= 4);
  }, [temperatureLogs]);

  const coachPositions = useMemo(() => {
    const positions = [];
    let locoCount = 0;
    let wag12Count = 0;
    let normalCount = 1;
    let skip = 0;

    for (let i = 0; i < temperatureLogs.length; i++) {
      if (skip > 0) {
        positions[i] = "";
        skip--;
        continue;
      }

      if (temperatureLogs[i].CoachPosition === "LOCO") {
        locoCount++;
        positions[i] = `LOCO ${locoCount}`;
        skip = 5;
      } else if (temperatureLogs[i].CoachPosition === "WAG12") {
        wag12Count++;
        positions[i] = `WAG12 ${Math.ceil(wag12Count / 2)}.${
          wag12Count % 2 === 1 ? 1 : 2
        }`;
        skip = 3;
      } else {
        if ((i - locoCount * 6 - wag12Count * 4) % 4 === 0) {
          positions[i] = temperatureLogs[i].CoachPosition;
          skip = 3;
        } else {
          positions[i] = "";
        }
      }
    }

    return positions;
  }, [temperatureLogs]);

  const coachRFIDs = useMemo(() => {
    const rfids = [];
    let locoCount = 0;
    let wag12Count = 0;
    let normalCount = 1;
    let skip = 0;

    for (let i = 0; i < temperatureLogs.length; i++) {
      if (skip > 0) {
        rfids[i] = "";
        skip--;
        continue;
      }

      if (temperatureLogs[i].CoachPosition === "LOCO") {
        locoCount++;
        rfids[i] = temperatureLogs[i].hex_decoded_id || "N/A";
        skip = 5;
      } else if (temperatureLogs[i].CoachPosition === "WAG12") {
        wag12Count++;
        rfids[i] = temperatureLogs[i].hex_decoded_id || "N/A";
        skip = 3;
      } else {
        if ((i - locoCount * 6 - wag12Count * 4) % 4 === 0) {
          rfids[i] = temperatureLogs[i].hex_decoded_id || "N/A";
          normalCount++;
          skip = 3;
        } else {
          rfids[i] = "";
        }
      }
    }

    return rfids;
  }, [temperatureLogs]);

  const coachNumbers = useMemo(() => {
    const numbers = [];
    let locoCount = 0;
    let wag12Count = 0;
    let normalCount = 1;
    let skip = 0;

    for (let i = 0; i < temperatureLogs.length; i++) {
      if (skip > 0) {
        numbers[i] = "";
        skip--;
        continue;
      }

      if (temperatureLogs[i].CoachPosition === "LOCO") {
        locoCount++;
        numbers[i] = temperatureLogs[i].CoachNo || "N/A";
        skip = 5;
      } else if (temperatureLogs[i].CoachPosition === "WAG12") {
        wag12Count++;
        numbers[i] = temperatureLogs[i].CoachNo || "N/A";
        skip = 3;
      } else {
        if ((i - locoCount * 6 - wag12Count * 4) % 4 === 0) {
          numbers[i] = temperatureLogs[i].CoachNo || "N/A";
          normalCount++;
          skip = 3;
        } else {
          numbers[i] = "";
        }
      }
    }

    return numbers;
  }, [temperatureLogs]);

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto overflow-y-auto max-h-[475px] scrollbar-custom">
        <table className="min-w-full">
          <thead className="sticky top-0 z-[4] bg-blue-900 text-white">
            <tr>
              <th className="px-2 py-2 text-center text-xs border-b border-gray-300">
                Axle No
              </th>
              <th className="px-2 py-2 text-center text-xs border-b border-gray-300">
                Coach Position
              </th>
              <th className="px-2 py-2 text-center text-xs border-b border-gray-300">
                Coach Number
              </th>
              <th className="px-2 py-2 text-center text-xs border-b border-gray-300">
                Coach RFID
              </th>
              {shouldShowAxleColumns && (
                <>
                  <th className="px-3 py-2 text-center text-xs border-b border-gray-300">
                    LH Axle (°C)
                  </th>
                  <th className="px-3 py-2 text-center text-xs border-b border-gray-300">
                    RH Axle (°C)
                  </th>
                  <th className="px-3 py-2 text-center text-xs border-b border-gray-300">
                    Axle Temp Diff (°C)
                  </th>
                  <th className="px-3 py-2 text-center text-xs border-b border-gray-300">
                    Avg Axle Temp (°C)
                  </th>
                </>
              )}
              {shouldShowWheelColumns && (
                <>
                  <th className="px-3 py-2 text-center text-xs border-b border-gray-300">
                    LH Wheel (°C)
                  </th>
                  <th className="px-3 py-2 text-center text-xs border-b border-gray-300">
                    RH Wheel (°C)
                  </th>
                  <th className="px-3 py-2 text-center text-xs border-b border-gray-300">
                    Wheel Temp Diff (°C)
                  </th>
                  <th className="px-3 py-2 text-center text-xs border-b border-gray-300">
                    Avg Wheel Temp (°C)
                  </th>
                </>
              )}
              <th className="px-3 py-2 text-center text-xs border-b border-gray-300">
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={handleDownload}
                    className="text-white hover:text-gray-300 transition"
                    aria-label="Download"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 
                          7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    ) : (
                      <FiDownload size={18} />
                    )}
                  </button>
                  <button
                    onClick={toggleCollapse}
                    className="text-white hover:text-gray-300 transition"
                    aria-label="Toggle Table"
                  >
                    {isCollapsed ? (
                      <FiChevronRight size={18} />
                    ) : (
                      <FiChevronDown size={18} />
                    )}
                  </button>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className={`transition-all ${isCollapsed ? "hidden" : ""}`}>
            {temperatureLogs.length > 0 ? (
              temperatureLogs.map((log, index) => {
                const hasValidRFID =
                  coachRFIDs[index] &&
                  coachRFIDs[index] !== "N/A" &&
                  coachRFIDs[index].trim() !== "";

                const hasDifferenceAlarm = alarms?.some(
                  (alarm) => alarm.AxelNo === log.AxleNo && !alarm.Side
                );

                const hasDifferenceWarning = warnings?.some(
                  (warning) => warning.AxelNo === log.AxleNo && !warning.Side
                );

                return (
                  <tr
                    key={log.TempLogID}
                    className={`hover:bg-gray-300 ${
                      hasDifferenceAlarm
                        ? "bg-red-100"
                        : hasDifferenceWarning
                        ? "bg-yellow-100"
                        : coachPositions[index]
                        ? "bg-gray-100"
                        : ""
                    }`}
                  >
                    <td className="px-3 py-2 text-center text-xs">
                      {log.AxleNo}
                    </td>
                    <td className="px-2 py-2 text-center text-xs">
                      {coachPositions[index]}
                    </td>
                    <td className="px-2 py-2 text-center text-xs">
                      {coachNumbers[index]}
                    </td>
                    <td
                      className="px-2 py-2 text-center text-xs relative"
                      onMouseEnter={() =>
                        hasValidRFID && setHoveredIndex(index)
                      }
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      {coachRFIDs[index]}
                      {hasValidRFID && hoveredIndex === index && (
                        <div className="absolute z-[3] bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-96 left-1/2 transform -translate-x-1/2 mt-2 bg-gradient-to-br from-blue-50 to-gray-50 transition-transform duration-200">
                          <h4 className="text-sm font-bold text-gray-800 mb-3">
                            RFID Details
                          </h4>
                          <div className="grid grid-cols-[120px_1fr] gap-x-4 gap-y-2 text-xs text-gray-700">
                            <span className="font-semibold text-gray-900 text-left">
                              Hex Decoded ID:
                            </span>
                            <span className="break-all">
                              {log.hex_decoded_id || "N/A"}
                            </span>
                            <span className="font-semibold text-gray-900 text-left">
                              Reader ID:
                            </span>
                            <span>{log.reader_id || "N/A"}</span>
                            <span className="font-semibold text-gray-900 text-left">
                              GS1 India:
                            </span>
                            <span>{log.GS1_India || "N/A"}</span>
                            <span className="font-semibold text-gray-900 text-left">
                              Version No:
                            </span>
                            <span>{log.version_no || "N/A"}</span>
                            <span className="font-semibold text-gray-900 text-left">
                              Vehicle Side:
                            </span>
                            <span>{log.vehicle_side || "N/A"}</span>
                            <span className="font-semibold text-gray-900 text-left">
                              Asset Type:
                            </span>
                            <span>{log.asset_type || "N/A"}</span>
                            <span className="font-semibold text-gray-900 text-left">
                              Manufacturing Year:
                            </span>
                            <span>{log.manufacturing_yr || "N/A"}</span>
                            <span className="font-semibold text-gray-900 text-left">
                              Owner Zone:
                            </span>
                            <span>{log.owner_zone || "N/A"}</span>
                            <span className="font-semibold text-gray-900 text-left">
                              Transportation Code:
                            </span>
                            <span>{log.transportation_code || "N/A"}</span>
                            <span className="font-semibold text-gray-900 text-left">
                              Serial No:
                            </span>
                            <span>{log.serial_no || "N/A"}</span>
                          </div>
                        </div>
                      )}
                    </td>
                    {shouldShowAxleColumns && (
                      <>
                        <td className="px-3 py-2 text-center text-xs">
                          <span className="inline-flex items-center">
                            {formatTemperature(log.Ts1)}
                            {getTempStatusIcon(log.Ts1Alarm, log.Ts1Warning)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center text-xs">
                          <span className="inline-flex items-center">
                            {formatTemperature(log.Ts2)}
                            {getTempStatusIcon(log.Ts2Alarm, log.Ts2Warning)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center text-xs">
                          {calculateTempDiff(log.Ts1, log.Ts2)}
                        </td>
                        <td className="px-3 py-2 text-center text-xs">
                          {calculateAverage(log.Ts1, log.Ts2)}
                        </td>
                      </>
                    )}
                    {shouldShowWheelColumns && (
                      <>
                        <td className="px-3 py-2 text-center text-xs">
                          <span className="inline-flex items-center">
                            {formatTemperature(log.Ts3)}
                            {getTempStatusIcon(log.Ts3Alarm, log.Ts3Warning)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center text-xs">
                          <span className="inline-flex items-center">
                            {formatTemperature(log.Ts4)}
                            {getTempStatusIcon(log.Ts4Alarm, log.Ts4Warning)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center text-xs">
                          {calculateTempDiff(log.Ts3, log.Ts4)}
                        </td>
                        <td className="px-3 py-2 text-center text-xs">
                          {calculateAverage(log.Ts3, log.Ts4)}
                        </td>
                      </>
                    )}
                    <td className="px-3 py-2 text-center text-xs"></td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={
                    4 +
                    (shouldShowAxleColumns ? 4 : 0) +
                    (shouldShowWheelColumns ? 4 : 0) +
                    1
                  }
                  className="px-4 py-2 text-center text-xs text-black"
                >
                  No Data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TempLogTable;
